import React, {
  forwardRef,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";
import { Canvas } from "@react-three/fiber";
import { Vector3 } from "three";

import { createContext } from "react";

/**
 * Props
 */
interface Sizes {
  width: number;
  height: number;
  aspect: number;
}

interface CameraProps {
  fov: number;
  near: number;
  far: number;
  position: Vector3;
}

interface CanvasProps {
  sizes: Sizes;
  setSizes: (sizes: Sizes) => void;
  camera: CameraProps;
}

interface Props extends PropsWithChildren {
  id: string;
  cameraOptions?: CameraProps;
  background?: string;
}

/**
 * Context
 */
const initialCamera = {
  fov: 45,
  near: 0.1,
  far: 1000,
  position: new Vector3(0, 0, 10),
};

const initialSizes = {
  width: window.innerWidth,
  height: window.innerHeight,
  aspect: window.innerWidth / window.innerHeight,
};

export const CanvasContext = createContext<CanvasProps>({
  camera: initialCamera,
  sizes: initialSizes,
  setSizes: () => {},
});

/**
 * Canvas
 */
const ResizingCanvas = forwardRef<HTMLCanvasElement, Props>(
  (
    { id, cameraOptions = initialCamera, background = "#f3f3f3", children },
    ref
  ) => {
    const [sizes, setSizes] = useState<Sizes>(initialSizes);

    // Canvas Size
    const styles: React.CSSProperties = {
      width: sizes.width,
      height: sizes.height,
    };

    // Canvas Resizing
    useEffect(() => {
      const resizeHandler = () => {
        setSizes({
          width: window.innerWidth,
          height: window.innerHeight,
          aspect: window.innerWidth / window.innerHeight,
        });
      };
      window.addEventListener("resize", resizeHandler);
      return () => window.removeEventListener("resize", resizeHandler);
    }, []);

    return (
      <CanvasContext.Provider
        value={{ sizes, setSizes, camera: cameraOptions }}
      >
        <Canvas ref={ref} id={id} style={styles} camera={cameraOptions}>
          <color attach="background" args={[background]} />
          {children}
        </Canvas>
      </CanvasContext.Provider>
    );
  }
);

export default ResizingCanvas;
